import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { ANNOTATION_PALETTE } from "@/lib/annotation-palette";

// ── Shape types ──────────────────────────────────────────────────────────────

// `pin` is intentionally NOT in this list any more — anchor-pin comments have
// their own flow that lives OUTSIDE annotation mode (see anchorMode below).
// The backend still accepts `pin` in the shape allowlist; keeping it there
// preserves legacy annotation blobs, we just don't create new ones from the UI.
export type AnnotationTool = "arrow" | "line" | "box" | "draw";

export type Shape =
  | { type: "arrow"; color: string; x1: number; y1: number; x2: number; y2: number }
  | { type: "line"; color: string; x1: number; y1: number; x2: number; y2: number }
  | { type: "box"; color: string; x: number; y: number; w: number; h: number }
  | { type: "draw"; color: string; points: [number, number][] };

// ── Context ──────────────────────────────────────────────────────────────────

type AnnotationContextValue = {
  /** True while the composer's annotation mode is active — canvas + toolbar render. */
  isActive: boolean;
  activate: () => void;
  deactivate: () => void;

  tool: AnnotationTool;
  setTool: (t: AnnotationTool) => void;
  color: string;
  setColor: (c: string) => void;

  /** Current set of shapes drawn but not yet submitted. */
  shapes: Shape[];
  /** Add a shape (drawing gesture completed). Clears redo stack. */
  addShape: (s: Shape) => void;
  /** Undo the last shape. */
  undo: () => void;
  /** Redo the last-undone shape. */
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  /** Drop all shapes + undo/redo state. Called on send success or manual clear. */
  clear: () => void;

  // ── Anchor pin (separate flow from annotation mode) ───────────────────────
  // Anchor mode: click on the video drops a single pin at that location and
  // opens a floating composer next to it. NOT a shape stored in an annotation
  // blob — the pin is persisted as the comment's `positionX`/`positionY`.
  /** True while user is in anchor-pin-drop mode (cursor becomes pin). */
  anchorMode: boolean;
  activateAnchor: () => void;
  deactivateAnchor: () => void;
  /** 0-1 normalized location of the dropped pin, or null before drop. */
  pendingPin: { x: number; y: number } | null;
  setPendingPin: (p: { x: number; y: number } | null) => void;
};

const AnnotationContext = createContext<AnnotationContextValue | null>(null);

export function useAnnotation(): AnnotationContextValue {
  const ctx = useContext(AnnotationContext);
  if (!ctx) throw new Error("useAnnotation must be used inside <AnnotationProvider>");
  return ctx;
}

// ── Provider ─────────────────────────────────────────────────────────────────

/**
 * Shared state for the annotation flow. Composer owns the "activate" button,
 * the video player renders the canvas overlay, and the toolbar reads/writes
 * tool + color. Lifting to context keeps those three components loosely coupled.
 */
export function AnnotationProvider({ children }: { children: React.ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const [tool, setTool] = useState<AnnotationTool>("arrow");
  const [color, setColor] = useState<string>(ANNOTATION_PALETTE[0].hex);
  const [shapes, setShapes] = useState<Shape[]>([]);
  const [redoStack, setRedoStack] = useState<Shape[]>([]);
  const [anchorMode, setAnchorMode] = useState(false);
  const [pendingPin, setPendingPin] = useState<{ x: number; y: number } | null>(null);

  const activate = useCallback(() => {
    // Anchor and annotation are mutually exclusive — activating one clears the other.
    setAnchorMode(false);
    setPendingPin(null);
    setIsActive(true);
  }, []);
  const deactivate = useCallback(() => setIsActive(false), []);
  const activateAnchor = useCallback(() => {
    setIsActive(false);
    setShapes([]);
    setRedoStack([]);
    setAnchorMode(true);
    setPendingPin(null);
  }, []);
  const deactivateAnchor = useCallback(() => {
    setAnchorMode(false);
    setPendingPin(null);
  }, []);

  const addShape = useCallback((s: Shape) => {
    setShapes((prev) => [...prev, s]);
    setRedoStack([]);   // any new stroke drops the redo history
  }, []);

  const undo = useCallback(() => {
    setShapes((prev) => {
      if (prev.length === 0) return prev;
      const last = prev[prev.length - 1];
      setRedoStack((r) => [...r, last]);
      return prev.slice(0, -1);
    });
  }, []);

  const redo = useCallback(() => {
    setRedoStack((r) => {
      if (r.length === 0) return r;
      const last = r[r.length - 1];
      setShapes((s) => [...s, last]);
      return r.slice(0, -1);
    });
  }, []);

  const clear = useCallback(() => {
    setShapes([]);
    setRedoStack([]);
  }, []);

  const value = useMemo<AnnotationContextValue>(
    () => ({
      isActive,
      activate,
      deactivate,
      tool,
      setTool,
      color,
      setColor,
      shapes,
      addShape,
      undo,
      redo,
      canUndo: shapes.length > 0,
      canRedo: redoStack.length > 0,
      clear,
      anchorMode,
      activateAnchor,
      deactivateAnchor,
      pendingPin,
      setPendingPin,
    }),
    [
      isActive, activate, deactivate,
      tool, color, shapes, addShape, undo, redo, redoStack.length, clear,
      anchorMode, activateAnchor, deactivateAnchor, pendingPin,
    ],
  );

  return <AnnotationContext.Provider value={value}>{children}</AnnotationContext.Provider>;
}
